"use client";

import { useTranslations } from "next-intl";
import { useMobileView } from "@/features/layout/useMobileView";
import { cn } from "@/lib/cn";

/**
 * The map/list switch, pinned to the bottom of the mobile viewport.
 *
 * Icons come from the `mappr-icon-font`, as in the legacy app. Its map glyph is
 * `ic-material_ic_map_24px` — the legacy component asked for `_48px`, which the
 * font doesn't contain, so that button rendered no icon at all.
 */
const VIEWS = [
  { value: "map", icon: "ic-material_ic_map_24px", label: "mapView" },
  { value: "list", icon: "ic-material_ic_menu_48px", label: "listView" },
] as const;

export function MobileViewToggle() {
  const t = useTranslations("mobile");
  const { view, setView, isPOIView } = useMobileView();

  // The POI panel covers the screen; a switch under it would be unreachable and
  // would still take focus.
  if (isPOIView) return null;

  return (
    <div className="fixed bottom-2 left-1/2 z-30 -translate-x-1/2">
      <div className="flex items-center gap-1 rounded-full border border-[rgba(0,0,0,0.1)] bg-white p-1 shadow-lg dark:bg-dark-color">
        {VIEWS.map(({ value, icon, label }) => {
          const isActive = view === value;

          return (
            <button
              key={value}
              type="button"
              aria-pressed={isActive}
              onClick={() => void setView(value)}
              className={cn(
                "flex items-center gap-2 rounded-full px-5 py-2 text-sm font-medium",
                "transition-all duration-200",
                isActive
                  ? "bg-white text-black shadow-sm dark:bg-dark-color dark:text-off-white"
                  : "text-gray-500 dark:text-gray-400"
              )}
            >
              <i className={`icon ${icon} text-[16px]`} aria-hidden="true" />
              {t(label)}
            </button>
          );
        })}
      </div>
    </div>
  );
}
